import { useI18n, useL } from "../i18n";
import { Shield, FileText, Cookie, UserCheck, Mail } from "lucide-react";

// Static AdSense-compliant policy pages. Written in AR/EN/FR.
export function PrivacyPage() {
  const { lang } = useI18n();
  const L = useL();
  const updated = "15 September 2026";

  return (
    <Wrapper>
      <Header
        icon={Shield}
        subtitle={L({ ar: "خصوصيتك", en: "Your privacy", fr: "Votre vie privée" })}
        title={L({ ar: "سياسة الخصوصية", en: "Privacy Policy", fr: "Politique de confidentialité" })}
        updated={updated}
      />

      <Section title={L({ ar: "من نحن", en: "Who we are", fr: "Qui sommes-nous" })}>
        {L({
          ar: "رحّال (Rahhal) هو دليل سفر إلكتروني للشرق الأوسط. نحرص على حماية بياناتك، ولا نبيعها لأي طرف ثالث.",
          en: "Rahhal is an online Middle East travel guide. We protect your data and never sell it to third parties.",
          fr: "Rahhal est un guide de voyage en ligne pour le Moyen-Orient. Nous protégeons vos données et ne les vendons jamais."
        })}
      </Section>

      <Section title={L({ ar: "البيانات التي نجمعها", en: "Data we collect", fr: "Données que nous collectons" })}>
        {L({
          ar: "لا نطلب حساباً لاستخدام الموقع. نخزّن محلياً على جهازك فقط: تفضيلات اللغة، وجهاتك المفضّلة، مخطط رحلاتك، ورمز إحالة مجهول. عند إرسال تقييم أو حفظ رحلة، تُخزّن تلك البيانات على خوادمنا. نستخدم Google AdSense لعرض إعلانات، وقد يستخدم Google ملفات تعريف الارتباط لعرض إعلانات ملائمة.",
          en: "No account is required. We store locally on your device only: language preference, favorites, trip plans and an anonymous referral code. Reviews and saved trips are stored on our servers. We use Google AdSense to serve ads; Google may use cookies to personalize ads.",
          fr: "Aucun compte requis. Nous stockons uniquement sur votre appareil : préférence linguistique, favoris, itinéraires et un code de parrainage anonyme. Les avis et voyages sauvegardés sont stockés sur nos serveurs. Nous utilisons Google AdSense ; Google peut utiliser des cookies pour personnaliser les annonces."
        })}
      </Section>

      <Section title={L({ ar: "ملفات تعريف الارتباط والإعلانات", en: "Cookies & ads", fr: "Cookies et publicités" })}>
        {L({
          ar: "يستخدم شركاؤنا الإعلانيون (Google AdSense) ملفات تعريف الارتباط لعرض إعلانات مخصصة بناءً على زياراتك السابقة لهذا الموقع أو مواقع أخرى. يمكنك تعطيل الإعلانات المخصصة عبر إعدادات إعلانات Google: adssettings.google.com",
          en: "Our advertising partners (Google AdSense) use cookies to serve ads based on your prior visits to this or other websites. You can opt out of personalized ads at: adssettings.google.com",
          fr: "Nos partenaires publicitaires (Google AdSense) utilisent des cookies pour diffuser des publicités personnalisées. Vous pouvez les désactiver sur : adssettings.google.com"
        })}
      </Section>

      <Section title={L({ ar: "روابط الشراكة", en: "Affiliate links", fr: "Liens d'affiliation" })}>
        {L({
          ar: "قد تحتوي بعض روابط الفنادق والطيران على معرّفات شراكة، ونتلقى عمولة عند إتمام الحجز عبرها بدون أي تكلفة إضافية عليك.",
          en: "Some hotel and flight links include affiliate IDs; we may earn a commission when you book through them at no extra cost to you.",
          fr: "Certains liens d'hôtels et vols contiennent des identifiants d'affiliation ; nous pouvons percevoir une commission sans frais supplémentaires pour vous."
        })}
      </Section>

      <Section title={L({ ar: "حقوقك", en: "Your rights", fr: "Vos droits" })}>
        {L({
          ar: "لك الحق في طلب حذف بياناتك بالتواصل معنا عبر صفحة الاتصال. يمكنك أيضاً مسح البيانات المحلية من إعدادات المتصفح في أي وقت.",
          en: "You may request deletion of your data via our contact page. You can also clear all local data from your browser settings at any time.",
          fr: "Vous pouvez demander la suppression de vos données via notre page de contact ou effacer les données locales depuis votre navigateur."
        })}
      </Section>
    </Wrapper>
  );
}

export function AboutPage() {
  const L = useL();
  return (
    <Wrapper>
      <Header
        icon={UserCheck}
        subtitle={L({ ar: "من نحن", en: "About us", fr: "À propos" })}
        title={L({ ar: "قصة رحّال", en: "The Rahhal Story", fr: "L'histoire de Rahhal" })}
      />

      <Section title={L({ ar: "رسالتنا", en: "Our mission", fr: "Notre mission" })}>
        {L({
          ar: "نصنع أول دليل عربي متكامل لأسعار السفر في الشرق الأوسط وشمال أفريقيا. نجمع البيانات من مصادر موثوقة، ونربطها بمساعد ذكاء اصطناعي يجاوبك بالعربية لحظياً — لتخطط رحلتك بثقة، وتعرف تكلفتها قبل الحجز.",
          en: "We build the first Arabic-first travel-price guide for the Middle East and North Africa. We pair curated data with an AI concierge that speaks your language, so you can plan a trip with confidence and know the cost before you book.",
          fr: "Nous créons le premier guide de prix arabophone pour le Moyen-Orient et l'Afrique du Nord. Données sélectionnées + concierge IA en arabe pour planifier en toute confiance et connaître le coût avant de réserver."
        })}
      </Section>

      <Section title={L({ ar: "ما نغطيه", en: "What we cover", fr: "Ce que nous couvrons" })}>
        {L({
          ar: "22 دولة عربية + تركيا، وأكثر من 20 وجهة أيقونية — من البتراء إلى سقطرى، ومن مراكش إلى بغداد. نعرض متوسط تكلفة الفندق والطعام والمواصلات والتذاكر لكل وجهة، مع طقس مباشر وآراء مسافرين حقيقيين.",
          en: "22 Arab countries + Turkey, 20+ iconic destinations — from Petra to Socotra, Marrakech to Baghdad. Live hotel, food, transport & ticket averages, real-time weather and honest traveler reviews.",
          fr: "22 pays arabes + Turquie, 20+ destinations emblématiques — de Pétra à Socotra, Marrakech à Bagdad. Moyennes hôtel/repas/transport/billets, météo en direct et avis authentiques."
        })}
      </Section>

      <Section title={L({ ar: "لماذا رحّال؟", en: "Why Rahhal?", fr: "Pourquoi Rahhal ?" })}>
        {L({
          ar: "لأن معظم أدلة السفر مكتوبة للسياح الغربيين. رحّال مصمم للمسافر العربي: أسعار بالدولار مع تحويل فوري لعملات المنطقة، محتوى بالعربية أولاً، ومساعد ذكاء اصطناعي يفهم لهجتك.",
          en: "Because most travel guides are written for Western tourists. Rahhal is built for Arab travelers: USD prices with instant regional currency conversion, Arabic-first content, and an AI concierge that gets your dialect.",
          fr: "La plupart des guides ciblent les Occidentaux. Rahhal est conçu pour les voyageurs arabes : prix en USD + conversion instantanée, contenu arabe-first, concierge IA qui comprend votre dialecte."
        })}
      </Section>
    </Wrapper>
  );
}

export function ContactPage() {
  const L = useL();
  return (
    <Wrapper>
      <Header
        icon={Mail}
        subtitle={L({ ar: "تواصل معنا", en: "Contact us", fr: "Contactez-nous" })}
        title={L({ ar: "نحن نستمع", en: "We're listening", fr: "Nous vous écoutons" })}
      />

      <div className="rounded-3xl bg-[#F3EDE4] p-6 sm:p-8 space-y-4">
        <p className="text-[#1C1917] leading-relaxed">
          {L({
            ar: "اقتراح لوجهة جديدة؟ ملاحظة على السعر؟ فرصة شراكة تجارية؟ يسعدنا سماعك.",
            en: "Have a destination suggestion? Price note? Partnership idea? We'd love to hear from you.",
            fr: "Une suggestion de destination ? Une remarque sur un prix ? Une idée de partenariat ? Écrivez-nous."
          })}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ContactCard
            label={L({ ar: "البريد العام", en: "General", fr: "Général" })}
            value="hello@rahhal.app"
            href="mailto:hello@rahhal.app"
          />
          <ContactCard
            label={L({ ar: "الشراكات", en: "Partnerships", fr: "Partenariats" })}
            value="partners@rahhal.app"
            href="mailto:partners@rahhal.app"
          />
          <ContactCard
            label={L({ ar: "الصحافة", en: "Press", fr: "Presse" })}
            value="press@rahhal.app"
            href="mailto:press@rahhal.app"
          />
          <ContactCard
            label={L({ ar: "الخصوصية", en: "Privacy", fr: "Confidentialité" })}
            value="privacy@rahhal.app"
            href="mailto:privacy@rahhal.app"
          />
        </div>

        <p className="text-xs text-[#78716C]">
          {L({
            ar: "نرد عادةً خلال 48 ساعة عمل.",
            en: "We reply within 48 business hours.",
            fr: "Nous répondons sous 48 heures ouvrées."
          })}
        </p>
      </div>
    </Wrapper>
  );
}

function Wrapper({ children }) {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6" data-testid="policy-page">
      {children}
    </div>
  );
}

function Header({ icon: Icon, subtitle, title, updated }) {
  const L = useL();
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#C85A32] font-semibold">{subtitle}</div>
      <h1 className="mt-2 font-display text-3xl sm:text-4xl text-[#1C1917] flex items-center gap-3">
        <Icon className="h-7 w-7 text-[#C85A32]" />
        {title}
      </h1>
      {updated && (
        <div className="mt-2 text-xs text-[#78716C]">
          {L({ ar: "آخر تحديث", en: "Last updated", fr: "Dernière mise à jour" })}: {updated}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-2xl bg-white border border-[#E6DED3] p-5 sm:p-6">
      <h2 className="font-display text-xl text-[#1C1917] mb-3">{title}</h2>
      <p className="text-[#57534E] leading-relaxed text-sm sm:text-base">{children}</p>
    </section>
  );
}

function ContactCard({ label, value, href }) {
  return (
    <a
      href={href}
      className="block rounded-2xl bg-white border border-[#E6DED3] hover:border-[#1C1917] p-4 transition-colors"
    >
      <div className="text-[10px] uppercase tracking-widest text-[#78716C]">{label}</div>
      <div className="mt-1 font-medium text-[#1C1917] break-all">{value}</div>
    </a>
  );
}
